import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";
import type { NewsItem } from "@/lib/wsTypes";
import { buildTokenLinkIndex, linkifyHeadline } from "@/lib/newsLinks";

/**
 * Scrolling competition news wire: the last 24 hours of published headlines.
 * When a genuinely-new item lands (after the initial snapshot settles) the
 * "Wire" pill flashes "Breaking". Token names in a headline link to that
 * token's market. "Scheduled" items (a known upcoming event) are marked.
 *
 * The wire reports events and nothing more — it never states direction or
 * severity. Reading that out of the prose is the game.
 */

const DAY_MS = 24n * 60n * 60n * 1000n;
const BREAKING_MS = 12_000;

const NewsEntry: FC<{
  item: NewsItem;
  index: ReturnType<typeof buildTokenLinkIndex>;
  onTradeMarket: (marketKey: string) => void;
  isNew?: boolean;
}> = ({ item, index, onTradeMarket, isNew }) => (
  <span className="mx-6 inline-flex items-center gap-2 align-middle">
    {isNew && (
      <span className="rounded bg-sr-salmon/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sr-salmon">
        New
      </span>
    )}
    <span className="rounded bg-sr-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sr-neutral-300">
      {item.token}
    </span>
    {item.announced && (
      <span className="rounded bg-sr-lemon-muted/15 px-1.5 py-0.5 text-[10px] font-medium text-sr-lemon-muted">
        scheduled
      </span>
    )}
    <span className="text-sm text-sr-neutral-200">{linkifyHeadline(item.headline, index, onTradeMarket)}</span>
  </span>
);

export const NewsTicker: FC<{ onTradeMarket: (marketKey: string) => void }> = ({ onTradeMarket }) => {
  const { ledgerView, markets } = useGummiwormContext();
  const news = (ledgerView?.news ?? []) as NewsItem[];
  const linkIndex = useMemo(() => buildTokenLinkIndex(markets), [markets]);

  const [now, setNow] = useState(() => BigInt(Date.now()));
  useEffect(() => {
    const t = setInterval(() => setNow(BigInt(Date.now())), 30_000);
    return () => clearInterval(t);
  }, []);

  const recent = useMemo(
    () => news.filter((n) => now - n.timeMs <= DAY_MS).slice(-40).reverse(),
    [news, now],
  );

  const baselineRef = useRef<bigint | null>(null);
  const [breakingUntil, setBreakingUntil] = useState(0);
  const newestTime = news.length ? news[news.length - 1].timeMs : 0n;
  useEffect(() => {
    if (baselineRef.current === null) {
      const t = setTimeout(() => {
        baselineRef.current = news.length ? news[news.length - 1].timeMs : 0n;
      }, 2500);
      return () => clearTimeout(t);
    }
    if (newestTime > baselineRef.current) {
      baselineRef.current = newestTime;
      setBreakingUntil(Date.now() + BREAKING_MS);
    }
  }, [newestTime, news]);

  const breaking = Date.now() < breakingUntil;
  const newestKey = recent.length ? `${recent[0].timeMs}:${recent[0].headline}` : "";

  if (recent.length === 0) return null;

  return (
    <div className="sr-panel flex items-center overflow-hidden" aria-label="Competition news wire">
      <div
        className={`flex shrink-0 items-center gap-1.5 border-r border-sr-border-subtle px-3 py-2 ${
          breaking ? "bg-sr-salmon/15" : ""
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${breaking ? "bg-sr-salmon" : "bg-sr-salmon/70"} animate-pulse`} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-sr-salmon">
          {breaking ? "Breaking" : "Wire"}
        </span>
      </div>
      <div className="group relative flex-1 overflow-hidden py-2">
        <div className="sr-marquee inline-block whitespace-nowrap group-hover:[animation-play-state:paused]">
          {recent.map((item, i) => (
            <NewsEntry
              key={`${item.timeMs}-${i}`}
              item={item}
              index={linkIndex}
              onTradeMarket={onTradeMarket}
              isNew={breaking && `${item.timeMs}:${item.headline}` === newestKey}
            />
          ))}
          {recent.map((item, i) => (
            <NewsEntry key={`dup-${item.timeMs}-${i}`} item={item} index={linkIndex} onTradeMarket={onTradeMarket} />
          ))}
        </div>
      </div>
    </div>
  );
};
